import { useState, useRef } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { CheckCircle, SkipForward, Clock, AlertTriangle, Loader2, Upload, FileUp, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

interface Task {
  id: string;
  title_snapshot: string;
  instruction_snapshot: string;
  status: string;
  due_at: string | null;
  is_critical_snapshot: boolean;
  required_proof_type_snapshot: string;
  sop_run: {
    name: string;
    sop_template: {
      title: string;
    };
  };
}

interface TaskDetailSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task: Task | null;
  userId: string;
  onSuccess: () => void;
}

export function TaskDetailSheet({
  open,
  onOpenChange,
  task,
  userId,
  onSuccess,
}: TaskDetailSheetProps) {
  const [completing, setCompleting] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const [proofText, setProofText] = useState("");
  const [skipReason, setSkipReason] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  if (!task) return null;

  const requiresProof = task.required_proof_type_snapshot !== "NONE";

  const handleComplete = async () => {
    const isTextProof = task.required_proof_type_snapshot === "TEXT" || task.required_proof_type_snapshot === "TEXT_NOTE";
    const isFileProof = task.required_proof_type_snapshot === "FILE" || task.required_proof_type_snapshot === "FILE_UPLOAD" || task.required_proof_type_snapshot === "PHOTO_UPLOAD";
    
    if (requiresProof && isTextProof && !proofText.trim()) {
      toast({
        variant: "destructive",
        title: "Note required",
        description: "Add a quick note before completing.",
      });
      return;
    }

    if (requiresProof && isFileProof && !file) {
      toast({
        variant: "destructive",
        title: "Upload required",
        description: "Please upload a file or photo.",
      });
      return;
    }

    setCompleting(true);
    try {
      let fileUrl: string | undefined;

      // Upload file if required
      if (isFileProof && file) {
        setUploading(true);
        const fileExt = file.name.split('.').pop() || 'file';
        
        // SECURITY: Get organization ID for org-scoped storage path
        const { data: membership } = await supabase
          .from('memberships')
          .select('organization_id')
          .eq('user_id', userId)
          .single();
        
        const orgId = membership?.organization_id || 'unknown';
        const fileName = `${orgId}/${task.id}/${Date.now()}.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from('proof-attachments')
          .upload(fileName, file, { cacheControl: '3600', upsert: false });

        if (uploadError) throw new Error(`Upload failed: ${uploadError.message}`);

        const { data: urlData } = supabase.storage
          .from('proof-attachments')
          .getPublicUrl(fileName);

        fileUrl = urlData.publicUrl;
        setUploading(false);
      }

      // Update task status
      const { error: taskError } = await supabase
        .from("sop_run_steps")
        .update({
          status: "COMPLETED",
          completed_at: new Date().toISOString(),
        })
        .eq("id", task.id);

      if (taskError) throw taskError;

      // Add proof if required
      if (requiresProof) {
        if (isTextProof && proofText.trim()) {
          const { error: proofError } = await supabase.from("proof_attachments").insert({
            sop_run_step_id: task.id,
            uploaded_by_user_id: userId,
            proof_type: "TEXT_NOTE" as const,
            text_value: proofText,
          });
          if (proofError) throw proofError;
        } else if (isFileProof && fileUrl) {
          const proofType: "PHOTO_UPLOAD" | "FILE_UPLOAD" = task.required_proof_type_snapshot === "PHOTO_UPLOAD" ? "PHOTO_UPLOAD" : "FILE_UPLOAD";
          const { error: proofError } = await supabase.from("proof_attachments").insert({
            sop_run_step_id: task.id,
            uploaded_by_user_id: userId,
            proof_type: proofType,
            file_url: fileUrl,
            text_value: file?.name,
          });
          if (proofError) throw proofError;
        }
      }

      toast({
        title: "Done. ✓",
        description: "Moving on.",
      });

      setProofText("");
      setFile(null);
      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Something broke",
        description: error.message || "Couldn't complete task. Try again.",
      });
    } finally {
      setCompleting(false);
      setUploading(false);
    }
  };

  const handleSkip = async () => {
    if (!skipReason.trim()) {
      toast({
        variant: "destructive",
        title: "Quick note needed",
        description: "Why are you skipping?",
      });
      return;
    }

    setSkipping(true);
    try {
      const { error } = await supabase
        .from("sop_run_steps")
        .update({
          status: "SKIPPED",
          skipped_by_user_id: userId,
          skipped_reason: skipReason,
        })
        .eq("id", task.id);

      if (error) throw error;

      toast({
        title: "Task Skipped",
        description: "The task has been skipped.",
      });

      setSkipReason("");
      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    } finally {
      setSkipping(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <div className="flex items-center gap-2">
            <SheetTitle className="text-xl">{task.title_snapshot}</SheetTitle>
            {task.is_critical_snapshot && (
              <Badge variant="destructive">Critical</Badge>
            )}
          </div>
          <SheetDescription>
            {task.sop_run?.sop_template?.title} • {task.sop_run?.name}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          <div className="flex items-center gap-4">
            <Badge
              className={
                task.status === "COMPLETED"
                  ? "bg-green-500/10 text-green-500"
                  : task.status === "IN_PROGRESS"
                  ? "bg-blue-500/10 text-blue-500"
                  : "bg-muted text-muted-foreground"
              }
            >
              {task.status.replace("_", " ")}
            </Badge>
            {task.due_at && (
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <Clock className="h-4 w-4" />
                Due {format(new Date(task.due_at), "MMM d, h:mm a")}
              </div>
            )}
          </div>

          <Separator />

          <div className="space-y-2">
            <Label className="text-sm font-medium">Instructions</Label>
            <div className="p-4 rounded-lg bg-muted/50 text-sm whitespace-pre-wrap">
              {task.instruction_snapshot || "No instructions provided."}
            </div>
          </div>

          {requiresProof && (
            <div className="space-y-3">
              <Label className="text-sm font-medium flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                Proof Required: {task.required_proof_type_snapshot.replace("_", " ")}
              </Label>
              {(task.required_proof_type_snapshot === "TEXT" || task.required_proof_type_snapshot === "TEXT_NOTE") && (
                <Textarea
                  value={proofText}
                  onChange={(e) => setProofText(e.target.value)}
                  placeholder="Quick note: what did you do?"
                  rows={3}
                  className="resize-none"
                />
              )}
              {(task.required_proof_type_snapshot === "FILE" || task.required_proof_type_snapshot === "FILE_UPLOAD" || task.required_proof_type_snapshot === "PHOTO_UPLOAD") && (
                <div className="space-y-3">
                  {file ? (
                    <div className="flex items-center gap-3 p-3 bg-muted rounded-xl">
                      <FileUp className="h-6 w-6 text-primary flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate text-sm">{file.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {(file.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setFile(null)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className={cn(
                        "w-full flex flex-col items-center justify-center gap-2 p-6",
                        "bg-muted/50 rounded-xl border-2 border-dashed border-border",
                        "hover:bg-muted hover:border-primary/30 transition-all",
                        "touch-target"
                      )}
                    >
                      <Upload className="h-8 w-8 text-muted-foreground" />
                      <span className="text-sm font-medium">Tap to upload</span>
                    </button>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={task.required_proof_type_snapshot === "PHOTO_UPLOAD" ? "image/*" : "*/*"}
                    capture={task.required_proof_type_snapshot === "PHOTO_UPLOAD" ? "environment" : undefined}
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    className="hidden"
                  />
                </div>
              )}
            </div>
          )}

          <Separator />

          <div className="flex flex-col gap-2">
            <Button onClick={handleComplete} disabled={completing} className="w-full">
              {completing ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle className="mr-2 h-4 w-4" />
              )}
              Complete Task
            </Button>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="w-full">
                  <SkipForward className="mr-2 h-4 w-4" />
                  Skip Task
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Skip Task</AlertDialogTitle>
                  <AlertDialogDescription>
                    Please provide a reason for skipping this task. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <div className="py-4">
                  <Textarea
                    value={skipReason}
                    onChange={(e) => setSkipReason(e.target.value)}
                    placeholder="Enter reason for skipping..."
                    rows={3}
                  />
                </div>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleSkip} disabled={skipping}>
                    {skipping && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Skip Task
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
